import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { applyService, ApplyStreamEvent } from '../services/apply.service';
import { resumeService } from '../services/resume.service';
import {
    ApplicationResult,
    TailorResult,
    Resume,
    ApplicationType,
    ResumeData,
    ApplicationSource,
    TailoredHistoryItem,
} from '../types';
import { ResumePreview } from '../components/ResumePreview';
import { Copy, Download, Zap, RefreshCw, X } from 'lucide-react';

const APPLICATION_TYPES: { value: ApplicationType; label: string }[] = [
    { value: 'upwork', label: 'Upwork proposal' },
    { value: 'linkedin', label: 'LinkedIn message' },
    { value: 'cover_letter', label: 'Cover letter' },
    { value: 'application_email', label: 'Application email' },
    { value: 'other', label: 'Other (custom label)' },
];

const LENGTH_OPTIONS = [
    { value: 'short', label: 'Short' },
    { value: 'medium', label: 'Medium' },
    { value: 'long', label: 'Long' },
];

const MAX_JOB_TEXT = 4000;

const formatSkills = (data: ResumeData) =>
    (data.skills?.categories || [])
        .map(category => `${category.name}: ${category.items.join(', ')}`)
        .filter(Boolean)
        .join(' — ');

const formatSummary = (data?: ResumeData) => data?.summary || '—';

const serializeSources = (sources: ApplicationSource[] = []) => {
    if (!sources.length) {
        return 'No source data provided.';
    }
    return sources
        .map(
            source =>
                `${source.text} (${source.fields.slice(0, 2).join(', ') || 'unknown field'})`
        )
        .join('\n');
};

export function ApplyingPage() {
    const [resumes, setResumes] = useState<Resume[]>([]);
    const [selectedResumeId, setSelectedResumeId] = useState<string>('');
    const [jobText, setJobText] = useState('');
    const [applicationType, setApplicationType] = useState<ApplicationType>('upwork');
    const [otherLabel, setOtherLabel] = useState('');
    const [tone, setTone] = useState('Professional');
    const [length, setLength] = useState('medium');
    const [tailorToggle, setTailorToggle] = useState(false);
    const [tailorIntensity, setTailorIntensity] = useState(1);
    const [generatedResult, setGeneratedResult] = useState<ApplicationResult | null>(null);
    const [tailoredResult, setTailoredResult] = useState<TailorResult | null>(null);
    const [progressLog, setProgressLog] = useState<string[]>([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [activeStage, setActiveStage] = useState<'tailor' | 'generate' | null>(null);
    const [requestId, setRequestId] = useState('');
    const [error, setError] = useState('');
    const [toastMessage, setToastMessage] = useState('');
    const [isSavingRatio, setIsSaving] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadFormat, setDownloadFormat] = useState<'pdf' | 'docx' | null>(null);
    const [isLoadingResumes, setIsLoadingResumes] = useState(true);
    const [, setTailoredHistory] = useState<TailoredHistoryItem[]>([]);
    const [, setIsLoadingHistory] = useState(true);
    const [currentTailoredShare, setCurrentTailoredShare] = useState<TailoredHistoryItem | null>(null);
    const [copiedShare, setCopiedShare] = useState(false);
    const [streamHandle, setStreamHandle] = useState<{ abort: () => void } | null>(null);
    const stageRef = useRef<'tailor' | 'generate' | null>(null);
    const tailoredRef = useRef<TailorResult | null>(null);

    const resumeMap = useMemo(() => new Map(resumes.map(resume => [resume.id, resume])), [resumes]);
    const selectedResume = useMemo(() => resumeMap.get(selectedResumeId) || resumes[0] || null, [
        resumeMap,
        resumes,
        selectedResumeId,
    ]);

    useEffect(() => {
        loadResumes();
        loadTailoredHistory();
    }, []);

    useEffect(() => {
        if (!selectedResumeId && resumes.length) {
            setSelectedResumeId(resumes[0].id);
        }
    }, [resumes, selectedResumeId]);

    useEffect(() => {
        if (!toastMessage) return;
        const timer = setTimeout(() => setToastMessage(''), 3000);
        return () => clearTimeout(timer);
    }, [toastMessage]);

    const loadResumes = async () => {
        setIsLoadingResumes(true);
        try {
            const data = await resumeService.getResumes(1, 50);
            setResumes(data.resumes);
        } catch (err) {
            console.error(err);
            setError('Unable to load resumes');
        } finally {
            setIsLoadingResumes(false);
        }
    };

    const loadTailoredHistory = async (): Promise<TailoredHistoryItem[]> => {
        setIsLoadingHistory(true);
        try {
            const data = await resumeService.getTailoredHistory(30);
            setTailoredHistory(data);
            return data;
        } catch (err) {
            console.error(err);
            return [];
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const handleStreamEvent = useCallback(
        (event: ApplyStreamEvent, payload: any) => {
            const prefix = stageRef.current ? `[${stageRef.current}] ` : '';
            if (event === 'progress') {
                const message = payload?.message || 'Working...';
                setProgressLog(prev => [...prev, `${prefix}${message}`].slice(-6));
            } else if (event === 'tailored') {
                const data = payload?.data as TailorResult;
                tailoredRef.current = data;
                setTailoredResult(data);
                setCurrentTailoredShare(null);
                setToastMessage('Tailored CV saved to history');
                if (payload?.requestId) {
                    setRequestId(payload.requestId);
                }
            } else if (event === 'result') {
                const data = payload?.data as ApplicationResult;
                setGeneratedResult(data);
                if (payload?.requestId) {
                    setRequestId(payload.requestId);
                }
                setToastMessage('Message saved to history');
            } else if (event === 'done') {
                if (payload?.requestId) {
                    setRequestId(payload.requestId);
                }
            }
        },
        []
    );

    const startStream = async (
        endpoint: 'generate' | 'tailor',
        body: any
    ): Promise<void> => {
        if (streamHandle) {
            streamHandle.abort();
        }
        const stream =
            endpoint === 'generate'
                ? applyService.streamGenerate(body, handleStreamEvent)
                : applyService.streamTailor(body, handleStreamEvent);
        setStreamHandle(stream);
        try {
            await stream.promise;
        } finally {
            setStreamHandle(null);
        }
    };

    const initiateGenerate = async (resumeJson?: ResumeData) => {
        const payload = {
            type: applicationType,
            jobText,
            resumeId: resumeJson ? undefined : selectedResumeId,
            resumeJson,
            tone,
            length,
            otherLabel: applicationType === 'other' ? otherLabel : undefined,
        };
        stageRef.current = 'generate';
        setActiveStage('generate');
        setProgressLog([]);
        await startStream('generate', payload);
    };

    const handleGenerate = async () => {
        if (!selectedResume) {
            setError('Please select a resume before generating an application.');
            return;
        }
        if (!jobText.trim()) {
            setError('Please add a job description before generating.');
            return;
        }
        setError('');
        setGeneratedResult(null);
        tailoredRef.current = null;
        setTailoredResult(null);
        setProgressLog([]);
        setRequestId('');
        setIsStreaming(true);
        if (tailorToggle) {
            await handleTailorSequence(false);
        } else {
            try {
                await initiateGenerate();
            } catch (err: any) {
                if (err.name !== 'AbortError') {
                    setError(err.message || 'Generation failed');
                }
            } finally {
                setIsStreaming(false);
                setActiveStage(null);
                stageRef.current = null;
            }
        }
    };

    const handleTailorSequence = async (triggerGenerate = true) => {
        if (!selectedResume) {
            setError('Please select a resume before tailoring.');
            return;
        }
        if (!jobText.trim()) {
            setError('Please add a job description before tailoring.');
            return;
        }
        setError('');
        setGeneratedResult(null);
        setProgressLog([]);
        setRequestId('');
        setIsStreaming(true);
        try {
            stageRef.current = 'tailor';
            setActiveStage('tailor');
            await startStream('tailor', {
                jobText,
                resumeId: selectedResumeId,
                templateId: selectedResume?.template,
                applicationType,
                intensity: tailorIntensity,
            });
            const tailored = tailoredRef.current;
            if (triggerGenerate && tailored) {
                await initiateGenerate(tailored.tailoredResume);
            }
            const history = await loadTailoredHistory();
            const historyId = tailoredRef.current?.historyId;
            if (historyId) {
                const match = history.find((item) => item.id === historyId);
                if (match) {
                    setCurrentTailoredShare(match);
                }
            }
        } catch (err: any) {
            if (err.name !== 'AbortError') {
                setError(err.message || 'Tailoring failed');
            }
        } finally {
            setIsStreaming(false);
            setActiveStage(null);
            stageRef.current = null;
        }
    };

    const handleTailorAndGenerate = async () => {
        await handleTailorSequence(true);
    };

    const handleCopy = async () => {
        if (!generatedResult?.generatedText) return;
        try {
            await navigator.clipboard.writeText(generatedResult.generatedText);
            setToastMessage('Copied to clipboard');
        } catch {
            setToastMessage('Unable to copy');
        }
    };

    const handleDownload = () => {
        if (!generatedResult?.generatedText) return;
        const blob = new Blob([generatedResult.generatedText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const name = generatedResult.companyName?.trim() || 'application';
        link.download = `${name}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleCoverLetterPdf = async () => {
        if (!generatedResult?.generatedText) return;
        setIsDownloading(true);
        setDownloadFormat('pdf');
        try {
            const companyName = generatedResult.companyName?.trim() || 'cover-letter';
            const blob = await resumeService.exportCoverLetterPdf({
                subject: generatedResult.generatedSubject,
                body: generatedResult.generatedText,
                title: companyName,
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${companyName}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error(err);
            setToastMessage('Unable to download PDF');
        } finally {
            setIsDownloading(false);
            setDownloadFormat(null);
        }
    };

    const handleSaveTailored = async () => {
        if (!tailoredResult || !selectedResume) return;
        setIsSaving(true);
        const title = `${selectedResume.title} (Tailored for ${applicationType === 'other' ? otherLabel || 'Application' : applicationType})`;
        try {
            await resumeService.createResume({
                title,
                template: selectedResume.template,
                data: tailoredResult.tailoredResume,
            });
            setToastMessage('Tailored resume saved as new version');
        } catch (err) {
            console.error(err);
            setToastMessage('Unable to save tailored resume');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownloadTailored = async (format: 'pdf' | 'docx') => {
        if (!tailoredResult || !selectedResume) return;
        setIsDownloading(true);
        setDownloadFormat(format);
        try {
            const companyName = generatedResult?.companyName?.trim() || tailoredResult?.companyName?.trim();
            const title = companyName
                ? `${companyName} - ${selectedResume.title}`
                : `${selectedResume.title} (Tailored)`;
            const blob = format === 'pdf'
                ? await resumeService.exportPdfFromData(tailoredResult.tailoredResume, selectedResume.template, title)
                : await resumeService.exportDocxFromData(tailoredResult.tailoredResume, title);

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${title}.${format}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error(err);
            setToastMessage(`Unable to download ${format.toUpperCase()}`);
        } finally {
            setIsDownloading(false);
            setDownloadFormat(null);
        }
    };

    const handleCancel = () => {
        if (streamHandle) {
            streamHandle.abort();
        }
        setIsStreaming(false);
        setActiveStage(null);
        stageRef.current = null;
    };


    const handleShareCurrent = async () => {
        const historyId = tailoredResult?.historyId;
        if (!historyId) {
            setToastMessage('Share link will appear after tailoring finishes.');
            return;
        }
        try {
            const updated = await resumeService.shareTailored(historyId);
            setCurrentTailoredShare(updated);
            setTailoredHistory((prev) =>
                prev.map((entry) => (entry.id === updated.id ? updated : entry))
            );
            if (updated.public_id) {
                const url = `${window.location.origin}/t/${updated.public_id}`;
                await navigator.clipboard.writeText(url);
                setToastMessage('Public link copied');
            }
        } catch (err) {
            console.error(err);
            setToastMessage('Unable to share tailored CV');
        }
    };

    const handleUnshareCurrent = async () => {
        const historyId = currentTailoredShare?.id || tailoredResult?.historyId;
        if (!historyId) return;
        try {
            const updated = await resumeService.unshareTailored(historyId);
            setCurrentTailoredShare(updated);
            setTailoredHistory((prev) =>
                prev.map((entry) => (entry.id === updated.id ? updated : entry))
            );
            setToastMessage('Sharing disabled');
        } catch (err) {
            console.error(err);
            setToastMessage('Unable to disable sharing');
        }
    };

    const charCount = jobText.length;
    const selectedLabel = applicationType === 'other' ? otherLabel : APPLICATION_TYPES.find(type => type.value === applicationType)?.label;
    const loadingLabel =
        activeStage === 'tailor'
            ? 'Tailoring your CV for this job...'
            : 'Generating your application message...';

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-3xl font-semibold">Applying</h2>
                <p className="text-sm text-gray-500">Generate messages that match your saved resumes.</p>
            </div>

            {toastMessage && (
                <div className="text-sm px-4 py-2 bg-green-50 text-green-800 border border-green-200 rounded">
                    {toastMessage}
                </div>
            )}

            {error && (
                <div className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded border border-red-200">
                    {error}
                </div>
            )}

            {isStreaming && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                    <div className="flex flex-col items-center gap-3 bg-white px-6 py-5 rounded-xl border border-blue-200 shadow-lg">
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-sm font-semibold text-gray-700">{loadingLabel}</p>
                        <p className="text-xs text-gray-500 text-center">
                            The AI is working on your request??"hang tight and avoid closing this window.
                        </p>
                        <button
                            className="btn btn-secondary flex items-center gap-2"
                            onClick={handleCancel}
                        >
                            <X size={16} />
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            <div className="grid gap-6 bg-white border border-gray-200 rounded-lg p-6">
                <div className="grid md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-1 text-sm font-semibold">
                        Application type
                        <select
                            className="input"
                            value={applicationType}
                            onChange={e => setApplicationType(e.target.value as ApplicationType)}
                        >
                            {APPLICATION_TYPES.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    {applicationType === 'other' && (
                        <label className="flex flex-col space-y-1 text-sm font-semibold">
                            Type label
                            <input
                                className="input"
                                placeholder="Describe the custom type"
                                value={otherLabel}
                                onChange={e => setOtherLabel(e.target.value)}
                            />
                        </label>
                    )}
                    <label className="flex flex-col space-y-1 text-sm font-semibold">
                        Resume
                        <select
                            className="input"
                            value={selectedResume?.id || ''}
                            onChange={e => setSelectedResumeId(e.target.value)}
                            disabled={isLoadingResumes}
                        >
                            <option value="" disabled>
                                {isLoadingResumes ? 'Loading...' : 'Select a resume'}
                            </option>
                            {resumes.map(resume => (
                                <option key={resume.id} value={resume.id}>
                                    {resume.title}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="flex flex-col space-y-1 text-sm font-semibold">
                        Tone
                        <input
                            className="input"
                            value={tone}
                            onChange={e => setTone(e.target.value)}
                            placeholder="e.g., Empathetic, Confident"
                        />
                    </label>
                    <label className="flex flex-col space-y-1 text-sm font-semibold">
                        Length
                        <select className="input" value={length} onChange={e => setLength(e.target.value)}>
                            {LENGTH_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
                    <label className="flex flex-col space-y-1 text-sm font-semibold">
                        Job description
                        <textarea
                            className="input min-h-[140px] resize-y"
                            value={jobText}
                        onChange={e => setJobText(e.target.value)}
                        placeholder="Paste the job description or listing here..."
                        maxLength={MAX_JOB_TEXT}
                        />
                        <div className="text-xs text-gray-500">{charCount}/{MAX_JOB_TEXT} characters</div>
                    </label>
                    <label className="flex flex-col space-y-2 text-sm font-semibold">
                        <div className="flex items-center gap-2">
                            <span>Tailor intensity</span>
                            <span className="relative group">
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-gray-300 text-xs text-gray-600">
                                    ?
                                </span>
                                <span className="pointer-events-none absolute left-1/2 top-7 -translate-x-1/2 w-64 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-normal text-gray-600 shadow-lg opacity-0 transition-opacity group-hover:opacity-100">
                                    Controls how strongly the resume is reworded toward the job. Higher means stronger alignment without adding false experience.
                                </span>
                            </span>
                        </div>
                        <input
                            type="range"
                            min={1}
                            max={10}
                            value={tailorIntensity}
                            onChange={(e) => setTailorIntensity(Number(e.target.value))}
                            className="h-2 accent-primary-600"
                        />
                        <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>Light (1)</span>
                            <span>Level {tailorIntensity}</span>
                            <span>Strong (10)</span>
                        </div>
                        <p className="text-xs text-gray-500">
                            Higher values align language more aggressively, without claiming untrue experience.
                        </p>
                    </label>
                <div className="flex items-center gap-4 flex-wrap">
                    <button
                        className="btn btn-primary"
                        onClick={handleGenerate}
                        disabled={isStreaming || !selectedResume || !jobText.trim()}
                    >
                        <Zap size={16} />
                        {`Generate${tailorToggle ? ' (Tailor enabled)' : ''}`}
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => handleTailorSequence(false)}
                        disabled={isStreaming || !selectedResume || !jobText.trim()}
                    >
                        <RefreshCw size={16} />
                        Tailor CV
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={handleTailorAndGenerate}
                        disabled={isStreaming || !selectedResume || !jobText.trim()}
                    >
                        <RefreshCw size={16} />
                        Tailor + Generate
                    </button>
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={tailorToggle}
                            onChange={e => setTailorToggle(e.target.checked)}
                        />
                        <span>Tailor my CV for this job</span>
                    </label>
                </div>
            </div>

            {(generatedResult?.generatedText || tailoredResult || isStreaming) && (
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Generated message</h2>
                        <div className="flex gap-2">
                            {applicationType !== 'application_email' && (
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={handleCopy}
                                    disabled={!generatedResult?.generatedText}
                                    aria-label="Copy text"
                                >
                                    <Copy size={16} />
                                </button>
                            )}
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={handleDownload}
                                disabled={!generatedResult?.generatedText}
                                aria-label="Download text"
                            >
                                <Download size={16} />
                            </button>
                            {applicationType === 'cover_letter' && (
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={handleCoverLetterPdf}
                                    disabled={!generatedResult?.generatedText || isDownloading}
                                    aria-label="Download PDF"
                                >
                                    <Download size={16} />
                                </button>
                            )}
                        </div>
                    </div>
                    {isStreaming && (
                        <div className="text-xs text-gray-500">
                            {activeStage === 'tailor' ? 'Tailoring resume...' : 'Generating application...'}
                        </div>
                    )}
                    {applicationType === 'application_email' ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <div className="flex-1 text-xs text-gray-500 border border-dashed rounded p-2 bg-gray-50">
                                    <span className="font-semibold">Subject:</span>{' '}
                                    {generatedResult?.generatedSubject || 'Subject will appear here.'}
                                </div>
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={async () => {
                                        if (!generatedResult?.generatedSubject) return;
                                        try {
                                            await navigator.clipboard.writeText(generatedResult.generatedSubject);
                                            setToastMessage('Subject copied');
                                        } catch {
                                            setToastMessage('Unable to copy subject');
                                        }
                                    }}
                                    disabled={!generatedResult?.generatedSubject}
                                    aria-label="Copy subject"
                                >
                                    <Copy size={16} />
                                </button>
                            </div>
                            <div className="flex items-start gap-2">
                                <div className="flex-1 min-h-[140px] text-sm text-gray-800 whitespace-pre-wrap border border-gray-100 rounded p-3 bg-white">
                                    {generatedResult?.generatedText || 'The email body will appear here.'}
                                </div>
                                <button
                                    className="btn btn-ghost btn-sm mt-1"
                                    onClick={handleCopy}
                                    disabled={!generatedResult?.generatedText}
                                    aria-label="Copy body"
                                >
                                    <Copy size={16} />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="min-h-[160px] text-sm text-gray-800 whitespace-pre-wrap">
                            {generatedResult?.generatedText || 'The generated message will appear here.'}
                        </div>
                    )}
                    {generatedResult?.sources?.length ? (
                        <div className="text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded p-3">
                            <p className="font-semibold mb-1">Sources used:</p>
                            <pre className="whitespace-pre-wrap text-xs">{serializeSources(generatedResult.sources)}</pre>
                        </div>
                    ) : (
                        <p className="text-xs text-gray-500">Sources will appear here after generation.</p>
                    )}
                    {requestId && (
                        <div className="text-xs text-gray-500">Request ID: {requestId}</div>
                    )}
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3">
                    <h2 className="text-lg font-semibold">Progress</h2>
                    {progressLog.length === 0 && <p className="text-xs text-gray-500">No activity yet.</p>}
                    <ul className="space-y-2 text-xs text-gray-600">
                        {progressLog.map((item, index) => (
                            <li key={`${item}-${index}`} className="leading-tight">
                                {item}
                            </li>
                        ))}
                    </ul>
                    {tailoredResult && (
                        <div className="bg-gray-50 border border-dashed rounded p-3 space-y-2">
                            <p className="text-xs text-gray-500">
                                Claims: {tailoredResult.claimsCheck.allowed ? 'All backed by resume' : 'Requires review'}
                            </p>
                            {tailoredResult.claimsCheck.newEntities?.length ? (
                                <div className="text-xs text-red-600">
                                    Added entities: {tailoredResult.claimsCheck.newEntities.map(entry => `${entry.category} (${entry.values.join(', ')})`).join('; ')}
                                </div>
                            ) : null}
                            {tailoredResult.sources?.length ? (
                                <pre className="text-xs text-gray-500 whitespace-pre-wrap">
                                    {serializeSources(tailoredResult.sources)}
                                </pre>
                            ) : null}
                        </div>
                    )}
                    </div>
                </div>
            )}

            {tailoredResult && selectedResume && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Differences</h2>
                        <button
                            className="btn btn-sm btn-secondary"
                            onClick={handleSaveTailored}
                            disabled={isSavingRatio}
                        >
                            Save tailored CV as new version
                        </button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="border border-gray-100 rounded-lg p-3">
                            <p className="text-sm font-semibold">Original</p>
                            <p className="text-xs text-gray-500 mb-2">{selectedResume.title}</p>
                            <p className="text-sm font-bold">Summary</p>
                            <p className="text-xs text-gray-600 mb-2">{formatSummary(selectedResume.data)}</p>
                            <p className="text-sm font-bold mt-2">Skills</p>
                            <p className="text-xs text-gray-600">{formatSkills(selectedResume.data) || 'No skills found'}</p>
                        </div>
                        <div className="border border-gray-100 rounded-lg p-3">
                            <p className="text-sm font-semibold">Tailored</p>
                            <p className="text-xs text-gray-500 mb-2">Derived from {selectedLabel}</p>
                            <p className="text-sm font-bold">Summary</p>
                            <p className="text-xs text-gray-600 mb-2">{formatSummary(tailoredResult.tailoredResume)}</p>
                            <p className="text-sm font-bold mt-2">Skills</p>
                            <p className="text-xs text-gray-600">
                                {formatSkills(tailoredResult.tailoredResume) || 'No skills included'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {tailoredResult && selectedResume && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">Tailored preview</h2>
                        <div className="flex gap-2">
                            {currentTailoredShare?.is_public ? (
                                <>
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={async () => {
                                    if (!currentTailoredShare.public_id) return;
                                    const url = `${window.location.origin}/t/${currentTailoredShare.public_id}`;
                                    await navigator.clipboard.writeText(url);
                                    setToastMessage('Public link copied');
                                    setCopiedShare(true);
                                    setTimeout(() => setCopiedShare(false), 1500);
                                }}
                            >
                                {copiedShare ? 'Copied' : 'Copy share link'}
                            </button>
                                    <button
                                        className="btn btn-sm btn-secondary"
                                        onClick={handleUnshareCurrent}
                                    >
                                        Disable link
                                    </button>
                                </>
                            ) : (
                                <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={handleShareCurrent}
                                >
                                    Share link
                                </button>
                            )}
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleDownloadTailored('pdf')}
                                disabled={isDownloading}
                            >
                                <Download size={16} />
                                {isDownloading && downloadFormat === 'pdf' ? 'Downloading...' : 'PDF'}
                            </button>
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleDownloadTailored('docx')}
                                disabled={isDownloading}
                            >
                                <Download size={16} />
                                {isDownloading && downloadFormat === 'docx' ? 'Downloading...' : 'DOCX'}
                            </button>
                        </div>
                    </div>
                    <div className="bg-gray-100 p-4 rounded-lg">
                        <div className="max-w-[8.5in] mx-auto">
                            <ResumePreview data={tailoredResult.tailoredResume} template={selectedResume.template} />
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
